import React, { useState, useEffect } from 'react';
import { useInstrumentForge } from '../InstrumentForgeContext';
import { useProductionConfig } from '@context/ProductionConfigContext';
import { useInstrumentProfiles } from '@hooks/useInstrumentProfiles';
import ProfileCard from './ProfileCard';
import ProfileBlender from './ProfileBlender';
import ProfileBrowser from './ProfileBrowser';
import ProductionDNARadar from './ProductionDNARadar';
import PhysicsPreview from './PhysicsPreview';
import { Zap, Layers, Sparkles, ChevronRight } from 'lucide-react';

const InstrumentForgeSelector: React.FC = () => {
  const { state, selectProfile, setBlendMode, clearProfile } = useInstrumentForge();
  const { applyInstrumentForge, clearInstrumentForge } = useProductionConfig();
  const { data: instrumentProfiles } = useInstrumentProfiles();
  const [applySuccess, setApplySuccess] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);

  // Auto-apply: PhysicsPreview below renders from local state.output the
  // instant a profile/blend is configured, with nothing in the UI
  // distinguishing "configured" from "actually pushed to the shared
  // ProductionConfigContext the prompt builder reads." That gap silently
  // dropped LAYER-2 through LAYER-5 from real songs whenever the separate
  // "Apply" click got missed. Configuring now IS applying -- previewing
  // and discarding before generating still works exactly the same, since
  // reconfiguring/clearing stays free right up until you hit Generate.
  useEffect(() => {
    if (!state.output) {
      clearInstrumentForge();
      return;
    }
    const configToApply = state.mode === 'blend'
      ? state.blendConfig!
      : {
          profileAKey: state.selectedProfileKey,
          profileBKey: null,
          blendRatio: 100
        };
    applyInstrumentForge(configToApply);
    setApplySuccess(true);
    const timer = setTimeout(() => setApplySuccess(false), 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.mode, state.selectedProfileKey, state.blendConfig, state.output]);

  const selectedProfile = state.selectedProfileKey ? instrumentProfiles[state.selectedProfileKey] : null;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-accent rounded-2xl text-slate-950 shadow-lg shadow-amber-500/20">
            <Layers size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-50 uppercase tracking-tighter leading-none">InstrumentForge&trade;</h2>
            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-2">Precision Production Architect</p>
          </div>
        </div>

        <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setBlendMode('single')}
            className={`px-6 py-2 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all ${state.mode === 'single' ? 'bg-slate-800 text-accent shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}
          >
            Single Profile
          </button>
          <button
            onClick={() => setBlendMode('blend')}
            className={`px-6 py-2 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all ${state.mode === 'blend' ? 'bg-slate-800 text-accent shadow-sm' : 'text-slate-400 hover:text-slate-300'}`}
          >
            Profile Blender
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 space-y-6">
          {state.mode === 'single' ? (
            <div className="bg-slate-900/40 p-6 rounded-3xl border-l-4 border-l-accent space-y-4 shadow-xl">
              <div className="flex justify-between items-center">
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block ml-1">Select Base Profile</label>
                <button onClick={clearProfile} className="text-[11px] font-bold text-slate-400 hover:text-red-400 uppercase transition-colors">Clear</button>
              </div>

              {/* Trigger button */}
              <button
                onClick={() => setBrowserOpen(true)}
                className="w-full flex items-center justify-between bg-slate-950 border-2 border-slate-800 rounded-2xl p-4 text-left hover:border-accent/50 transition-all group"
              >
                <span className={`text-lg font-black uppercase tracking-tight ${selectedProfile ? 'text-accent' : 'text-slate-400'}`}>
                  {selectedProfile ? selectedProfile.name : 'Browse Profiles...'}
                </span>
                <ChevronRight size={20} className="text-slate-400 group-hover:text-accent transition-colors" />
              </button>

              {selectedProfile && (
                <div className="animate-in slide-in-from-top-2">
                  <ProfileCard profile={selectedProfile} />
                </div>
              )}

              <ProfileBrowser
                isOpen={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onSelect={(key) => selectProfile(key)}
                selectedKey={state.selectedProfileKey}
                contextLabel="Select Base Profile"
              />
            </div>
          ) : (
            <div className="bg-slate-900/40 p-6 rounded-3xl border-l-4 border-l-accent shadow-xl">
              <ProfileBlender />
            </div>
          )}

          {state.output && (
            <div className="space-y-4">
              <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                <Sparkles size={14} className="text-accent" /> Generated Physics Architecture
              </h3>
              <PhysicsPreview output={state.output} />
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-6">
          {state.output && (
            <>
              <ProductionDNARadar traits={state.output.traits} />
              <div
                className={`w-full py-4 font-black uppercase text-sm tracking-widest rounded-2xl shadow-xl flex items-center justify-center gap-3 transition-colors ${
                  applySuccess
                    ? 'bg-green-600 text-white shadow-green-900/20'
                    : 'bg-slate-900/60 border border-accent/30 text-accent shadow-amber-900/10'
                }`}
              >
                <Zap size={18} fill="currentColor" />
                {applySuccess ? 'Architecture Synchronized' : 'Live in Studio — auto-synced'}
              </div>
              <div className="bg-slate-900/40 p-5 rounded-2xl border-l-4 border-l-accent">
                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">Architect's Summary</h4>
                <p className="text-[11px] text-slate-400 leading-relaxed italic">
                  {state.output.description} Optimized for Suno V5 'Chirp-Crow' pattern recognition. Physics layers provide deterministic structural constraints while machine tags guide the semantic vibe.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstrumentForgeSelector;
